/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import pipe from 'lodash/fp/pipe.js';
import { head } from 'ramda';
import { connect } from 'react-redux';
import withNavigate from '#components/support/routing/withNavigate/index.jsx';
import withLocation from '#components/support/routing/withLocation/index.jsx';
import {
  selectAuthUserId,
  selectAuthUserModelTypes,
  selectAuthUserOrgHasExtractionAccess,
  selectAuthUserOrgId,
  selectAuthUserPermissions,
} from '#selectors/entities/users.js';
import { createModel } from '#actions/models/model.js';
import { startCreateModelFlow } from '#actions/models/createModelFlow.js';
import { MODELS, MODEL_TYPES, UPLOAD_DECISION } from '#constants/pathStrings.js';
import { EXTRACTION_CAPABLE_TYPES } from '#constants/models/extractionCapableTypes.js';
import CreateModelButton from './CreateModelButton.jsx';

export const mapStateToProps = state => ({
  userModelTypes: selectAuthUserModelTypes(state),
  createdById: selectAuthUserId(state),
  hasExtractionAccess: selectAuthUserOrgHasExtractionAccess(state),
  orgId: selectAuthUserOrgId(state),
  permissions: selectAuthUserPermissions(state),
});

const mapDispatchToProps = { createModel, startCreateModelFlow };

// When more than one model type is allowed, go to type selection; when only one is allowed, skip
// it and create the model immediately (UI design §2.1; issue #127 removed the interim naming step),
// then route into the create flow's decision step (HBR-4950 #316) for the new model -- applying the
// same EXTRACTION_CAPABLE_TYPES filter as multi-type selection (a deliberate consistency fix: the
// single-type shortcut previously sent a single-type, extraction-permitted Site Hazards org to the
// upload step even though type 3 has no aiExposed fields to extract). The single-type branch
// returns the create promise so the button can show its in-flight spinner.
export const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { userModelTypes, createdById, hasExtractionAccess, orgId, permissions } = stateProps;
  const { navigate, location } = ownProps;
  const { search } = location;
  const allow = search ? new URLSearchParams(search).get('allow') : 'false';

  const handleClick = () => {
    if (allow === 'true' || userModelTypes.length > 1) {
      navigate(`${MODEL_TYPES}`);
      return null;
    }
    const type = head(userModelTypes);
    return dispatchProps.createModel(type, createdById, orgId, permissions).then(model => {
      if (EXTRACTION_CAPABLE_TYPES.includes(type) && hasExtractionAccess) {
        dispatchProps.startCreateModelFlow(model.uuid);
        navigate(`/${MODELS}/${model.uuid}/${UPLOAD_DECISION}`);
      } else {
        navigate(`/${MODELS}/${model.uuid}`);
      }
      return model;
    });
  };

  return {
    ...ownProps,
    handleClick,
  };
};

const CreateModelButtonContainer = pipe(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  withNavigate,
  withLocation
)(CreateModelButton);

export default CreateModelButtonContainer;
